import React, { useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import isEqual from 'lodash/isEqual';
import WidgetUpdateOptions from './WidgetUpdateOptions';
import { Close } from '@blueprintnyc/bpnyc-common-admin/dist/icons';
import { getWidgetKey } from '@/redux/reducers/widget.reducer';

const WidgetUpdate = props => {
	const { event, widgetToUpdate, setWidgetToUpdate, handleClose, onUpdate, isSaving } = props;
	const { type } = widgetToUpdate;
	const selectedWidgetKey = widgetToUpdate.widgetKey || getWidgetKey(widgetToUpdate);
	const [omniPicker, setOmniPicker] = useState({
		open: false,
		mediaPicker: false,
		from: '',
		index: null,
	});
	const [prevWidgetKey, setPrevWidgetKey] = useState(selectedWidgetKey);
	const [widgetProps, setWidgetProps] = useState(widgetToUpdate.props || {});
	const [targetKeys, setTargetKeys] = useState(widgetToUpdate.groups || []);
	// A ref, because an upload started before Save calls back through the closure of an older render.
	const isSavingRef = useRef(isSaving);
	isSavingRef.current = isSaving;
	const changeDuringSaveRef = useRef(null);

	if (prevWidgetKey !== selectedWidgetKey) {
		setPrevWidgetKey(selectedWidgetKey);
		setWidgetProps(widgetToUpdate.props || {});
		setTargetKeys(widgetToUpdate.groups || []);
	}

	const commitWidgetChange = (nextWidgetProps, nextGroups = targetKeys) => {
		if (isSavingRef.current) {
			changeDuringSaveRef.current = { props: nextWidgetProps, groups: nextGroups };
			return nextWidgetProps;
		}
		const currentWidgetProps = widgetToUpdate.props || {};
		const currentWidgetGroups = widgetToUpdate.groups || [];
		if (isEqual(nextWidgetProps, currentWidgetProps) && isEqual(nextGroups, currentWidgetGroups))
			return nextWidgetProps;

		setTargetKeys(nextGroups);
		setWidgetToUpdate(currentWidget => ({
			...currentWidget,
			props: nextWidgetProps,
			groups: nextGroups,
			toUpdate: !!currentWidget.id,
		}));
		onUpdate({ props: nextWidgetProps, groups: nextGroups, closeOptions: false });
		return nextWidgetProps;
	};

	const applyWidgetChange = (nextWidgetProps, nextGroups = targetKeys) => {
		const result = commitWidgetChange(nextWidgetProps, nextGroups);
		if (!isEqual(result, widgetProps)) setWidgetProps(result);
	};

	// The save's acks would overwrite a change committed mid-save, so it waits for the save.
	useEffect(() => {
		if (isSaving || !changeDuringSaveRef.current) return;
		const { props: deferredProps, groups } = changeDuringSaveRef.current;
		changeDuringSaveRef.current = null;
		applyWidgetChange(deferredProps, groups);
	}, [isSaving]);

	const handleGroupChange = (direction, ids) =>
		applyWidgetChange(
			widgetProps,
			ids.rightIDs.map(id => parseInt(id))
		);

	const handleChange = values => applyWidgetChange({ ...widgetProps, ...values });

	const handlePropChange = propName => value =>
		applyWidgetChange({ ...widgetProps, [propName]: value });

	const handlePickOmniItem = pickedLink => {
		setOmniPicker({ open: false, mediaPicker: false, from: '', index: null });
		handlePropChange('onPress')(pickedLink);
	};

	const handlePickMedia = (media, from) => () => {
		if (media.mime_type && media.mime_type.split('/')[0] === 'video') {
			notification.error({
				message: 'Error',
				description: 'Videos on the home screen are not currently supported.',
			});
		} else {
			if (from === 'media') {
				const src =
					media && media.src
						? {
								src: media.src,
								name: media.name,
								mime_type: media.mime_type,
						  }
						: null;
				handlePropChange('source')(src);
			} else {
				handlePropChange('onPress')({ link: media.src });
			}
			setOmniPicker({ open: false, mediaPicker: false, from: '' });
		}
	};

	return (
		<div className="WidgetUpdateOptions">
			<span className="header-text">{type} Widget Options</span>
			<div className="widget-close-button" onClick={handleClose}>
				<Close color="gray" />
			</div>
			<WidgetUpdateOptions
				key={selectedWidgetKey}
				type={type}
				event={event}
				handlePropChange={handlePropChange}
				omniPicker={omniPicker}
				setOmniPicker={setOmniPicker}
				handlePickOmniItem={handlePickOmniItem}
				handlePickMedia={handlePickMedia}
				widgetProps={widgetProps}
				handleChange={handleChange}
				syncWidgetChange={commitWidgetChange}
				setWidgetToUpdate={setWidgetToUpdate}
				targetKeys={targetKeys}
				handleGroupChange={handleGroupChange}
				{...props}
			/>
		</div>
	);
};

export default WidgetUpdate;
